import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Card from '../components/Card';
import Features from '../components/Features';
import MetaTags from '../components/MetaTags';

const Home = () => {
  return (
    <Layout title="AskTide">
      <MetaTags 
        title="AskTide - Professional Live Q&A Platform for Broadcasters"
        description="Elevate your broadcasts with professional Q&A management. Real-time audience interaction, broadcast-ready displays, and seamless integration with OBS, vMix, and Tricaster. Perfect for live events, shows, and presentations."
        url={window.location.href}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative bg-gray-50 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="relative mb-24 z-10 lg:grid lg:grid-cols-2 lg:gap-8 items-center">
              <div className="relative pb-8 sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
                <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                  <div className="sm:text-center lg:text-left">
                    <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                      <span className="block">Live Q&A Made</span>
                      <span className="block text-blue-600">Simple and Engaging</span>
                    </h1>
                    <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                      Manage live questions, engage with your audience, and create interactive sessions with ease.
                    </p>
                    <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                      <div className="rounded-md shadow">
                        <Link
                          to="/create"
                          className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10"
                          data-umami-event="Create Session - Hero"
                        >
                          Create Session
                        </Link>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:ml-3">
                        <Link
                          to="/join"
                          className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 md:py-4 md:text-lg md:px-10"
                          data-umami-event="Join Session - Hero"
                        >
                          Join Session
                        </Link>
                      </div>
                    </div>
                    
                    {/* Mobile Video - Only visible on mobile and hidden on desktop */}
                    <div className="mt-8 lg:hidden">
                      <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-xl">
                        <video
                          className="w-full h-full object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                        >
                          <source src="/features/asktide.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  </div>
                </main>
              </div>
              <div className="relative hidden lg:block">
                <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-xl">
                  <video
                    className="w-full h-full object-cover"
                    autoPlay
                    loop
                    muted
                    playsInline
                  >
                    <source src="/features/asktide.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <Features />

        <div className="max-w-4xl mx-auto">
         
          <div className="bg-primary-50 rounded-lg p-8 mb-24 mt-24">
            <h2 className="text-2xl font-bold text-center mb-6">How It Works</h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary-100 rounded-full w-10 h-10 flex items-center justify-center mr-4">
                  <span className="text-primary-800 font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-1">Create a Session</h3>
                  <p className="text-gray-600">
                    Start by creating a new session with a name and password. This creates a dedicated space for your event's questions.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary-100 rounded-full w-10 h-10 flex items-center justify-center mr-4">
                  <span className="text-primary-800 font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-1">Collect Questions</h3>
                  <p className="text-gray-600">
                    Share the session link with your audience. They can submit questions that will appear in your question manager in real-time.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary-100 rounded-full w-10 h-10 flex items-center justify-center mr-4">
                  <span className="text-primary-800 font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-1">Manage Questions</h3>
                  <p className="text-gray-600">
                    Review incoming questions, select the ones you want to answer, and move them through your workflow (New → Selected → On Air → Done).
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary-100 rounded-full w-10 h-10 flex items-center justify-center mr-4">
                  <span className="text-primary-800 font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-1">Display Questions</h3>
                  <p className="text-gray-600">
                    Questions marked as "On Air" will automatically appear on the display view, which you can show on your presentation screen.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-primary-100 rounded-full w-10 h-10 flex items-center justify-center mr-4">
                  <span className="text-primary-800 font-bold">5</span>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-1">Customize Templates</h3>
                  <p className="text-gray-600">
                    Create and save custom display templates to match your brand or event style. Apply different templates to different sessions.
                  </p>
                </div>
              </div>
            </div>
          </div>

         
        </div>

        {/* Pricing and About Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Pricing Column */}
              <div id="pricing">
                <div className="h-full">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                      Simple Pricing
                    </h2>
                    <p className="mt-4 text-xl text-gray-600">
                      Free during Beta period
                    </p>
                  </div>
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full">
                    <div className="px-6 py-8">
                      <div className="flex items-center justify-center">
                        <span className="px-3 py-1 text-sm text-primary-600 bg-primary-100 rounded-full">
                          BETA
                        </span>
                      </div>
                      <div className="mt-4 flex justify-center items-baseline">
                        <span className="text-5xl font-extrabold text-gray-900">$0</span>
                        <span className="ml-1 text-xl text-gray-500">/month</span>
                      </div>
                      <p className="mt-5 text-lg text-gray-500 text-center">
                        Full access to all features during our beta period
                      </p>
                      <div className="mt-8">
                        <ul className="space-y-4">
                          <li className="flex items-center">
                            <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="ml-3 text-gray-600">Unlimited sessions</span>
                          </li>
                          <li className="flex items-center">
                            <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="ml-3 text-gray-600">Custom templates</span>
                          </li>
                          <li className="flex items-center">
                            <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="ml-3 text-gray-600">Broadcast software integration</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* About Column */}
              <div id="about">
                <div className="h-full">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                      Why I Created AskTide
                    </h2>
                  </div>
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full">
                    <div className="px-6 py-8">
                      <div className="space-y-6">
                        <p className="text-lg text-gray-600 leading-relaxed">
                          As someone deeply involved in broadcast production, I experienced firsthand the challenges of managing audience questions during live events. The existing solutions were either too complex, didn't integrate well with broadcast software, or lacked the professional features needed in a broadcast environment.
                        </p>
                        <p className="text-lg text-gray-600 leading-relaxed">
                          I created AskTide Question Manager to solve these pain points, focusing specifically on the needs of broadcasters and live event producers. The goal was to create a tool that would make it incredibly simple to collect questions and seamlessly integrate them as overlay graphics in broadcast software like OBS, vMix, and Tricaster.
                        </p>
                        <p className="text-lg text-gray-600 leading-relaxed">
                          What makes AskTide different is its focus on broadcast-ready displays, real-time updates, and a streamlined workflow that lets you focus on your content rather than wrestling with technology.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-blue-600 py-16 mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                Ready to Transform Your Live Q&A?
              </h2>
              <p className="mt-4 text-xl text-blue-100">
                Start engaging with your audience in real-time. Perfect for broadcasts, live events, and presentations.
              </p>
              <div className="mt-8 flex justify-center space-x-4">
                <Link
                  to="/create"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 md:py-4 md:text-lg md:px-8"
                >
                  Create Your First Session
                </Link>
                <Link
                  to="/join"
                  className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-blue-700 md:py-4 md:text-lg md:px-8"
                >
                  Join a Session
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Home; 